import { useState } from "react";
import { CAMPO } from "../componentes/ui";
import {
  REGIMES,
  excluirCliente,
  rotuloDoRegime,
  salvarCliente,
  type Cliente,
  type Regime,
} from "../lib/calendario";
import { mascararTelefone, soDigitos } from "../lib/mascaras";

/**
 * Os clientes do contador, no Calendário (000106).
 *
 * ## Por que uma tabela própria, e não os clientes do MEI PRO
 *
 * Duas razões medidas, e a segunda é a decisiva:
 *
 * 1. **17.482 alunos têm o Calendário e NÃO têm o MEI PRO** (medido em 25/08
 *    por `vw_acesso_efetivo`). A leitura de
 *    `mei_meus_clientes` pergunta `conteudo_liberado('mei-pro')` por dentro da
 *    função — reusá-la deixaria **um terço** do público desta ferramenta sem
 *    clientes, e o defeito só apareceria com um aluno que tem um produto e não o
 *    outro (a cicatriz do portão que nomeia um card, agora na QUARTA vez nesta
 *    casa: 000090, 000098, o Simulador MEI e esta);
 * 2. **`mei_clientes.cliente_id` aponta para `profiles`**, cujo `email` é NOT
 *    NULL UNIQUE — porque no MEI o cliente LOGA para ver o próprio livro. Aqui o
 *    cliente não loga: ele é um rótulo de filtro. Reusar criaria uma linha de
 *    PESSOA, com conta, para cada nome que o contador quisesse filtrar.
 *
 * ## O cliente aqui é um RÓTULO, e a tela diz isso
 *
 * Ele existe para duas coisas: filtrar o calendário pelo regime dele e guardar
 * o que já foi feito naquele mês. E-mail e telefone são opcionais e servem ao
 * contador se ele quiser ter o contato à mão — nada é enviado a ninguém, e a
 * tela afirma isso, porque um campo de e-mail sem explicação faz o contador
 * supor que a ferramenta avisa o cliente.
 *
 * ## A posse nunca vem da tela
 *
 * Escrita é por RPC (`calendario_cliente_salvar`, `calendario_cliente_excluir`),
 * que resolvem o dono por `meu_profile_id()`. No app de origem o endpoint era um
 * POST público que recebia `aluno_email` e conferia a posse contra o e-mail que o
 * próprio chamador mandara — quem trocasse o e-mail lia e escrevia na carteira de
 * clientes de qualquer aluno.
 */
export default function CalendarioClientes({
  clientes,
  onMudou,
}: {
  clientes: Cliente[];
  onMudou: () => void;
}) {
  const [editando, setEditando] = useState<Cliente | "novo" | null>(null);
  const [nome, setNome] = useState("");
  const [regime, setRegime] = useState<Regime>("simples_nacional");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);

  function abrir(c: Cliente | "novo") {
    setEditando(c);
    setErro(null);
    setNome(c === "novo" ? "" : c.nome);
    setRegime(c === "novo" ? "simples_nacional" : c.regime);
    setEmail(c === "novo" ? "" : (c.email ?? ""));
    setTelefone(c === "novo" ? "" : mascararTelefone(c.telefone ?? ""));
  }

  async function salvar() {
    if (!nome.trim()) {
      setErro("Escreva o nome do cliente.");
      return;
    }
    setSalvando(true);
    setErro(null);
    // O telefone viaja em DÍGITOS, nunca como o texto mascarado — é a cicatriz
    // do campo de moeda do orçamento por outro caminho: o que aparece na tela e
    // o que vai ao banco são coisas diferentes, e a conversão é na fronteira.
    const r = await salvarCliente({
      id: editando === "novo" ? null : (editando?.id ?? null),
      nome: nome.trim(),
      regime,
      email,
      telefone: soDigitos(telefone),
    });
    setSalvando(false);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    setEditando(null);
    onMudou();
  }

  async function excluir(id: string) {
    setSalvando(true);
    const r = await excluirCliente(id);
    setSalvando(false);
    setConfirmando(null);
    if (!r.ok) {
      setErro(r.mensagem);
      return;
    }
    onMudou();
  }

  return (
    <div className="py-6">
      <div className="flex flex-wrap items-end justify-between gap-3 pb-4">
        <div className="max-w-xl">
          <h2 className="text-lg font-semibold text-titulo">Meus clientes</h2>
          <p className="pt-1.5 text-sm leading-relaxed text-cinza">
            Cadastre os clientes para filtrar o calendário pelo regime de cada um e marcar o
            que já foi entregue no mês. Só você vê esta lista, e{" "}
            <strong className="text-tinta">nada é enviado ao cliente</strong> — e-mail e
            telefone ficam aqui para você ter o contato à mão.
          </p>
        </div>
        <button
          type="button"
          onClick={() => abrir("novo")}
          className="rounded-full bg-laranja px-5 py-2.5 text-sm font-semibold text-navy-ink transition hover:brightness-105 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
        >
          Novo cliente
        </button>
      </div>

      {erro && !editando && (
        <p className="mb-4 rounded-xl bg-erro-fundo p-4 text-sm text-erro-tinta">{erro}</p>
      )}

      {editando && (
        <section
          role="group"
          aria-labelledby="form-cliente"
          className="mb-5 rounded-2xl border-[1.5px] border-borda bg-superficie p-5"
        >
          <h3 id="form-cliente" className="pb-4 text-sm font-semibold text-titulo">
            {editando === "novo" ? "Novo cliente" : `Editar ${editando.nome}`}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-tinta">Nome</span>
              <input
                className={CAMPO}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                maxLength={200}
                placeholder="Padaria do Bairro Ltda"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-tinta">Regime</span>
              <select
                className={CAMPO}
                value={regime}
                onChange={(e) => setRegime(e.target.value as Regime)}
              >
                {REGIMES.map((r) => (
                  <option key={r.valor} value={r.valor}>
                    {r.rotulo}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-tinta">
                Telefone <span className="font-normal text-cinza">(opcional)</span>
              </span>
              <input
                className={CAMPO}
                value={telefone}
                onChange={(e) => setTelefone(mascararTelefone(e.target.value))}
                inputMode="tel"
                placeholder="(11) 98765-4321"
              />
            </label>
            <label className="flex flex-col gap-1.5 text-sm sm:col-span-2">
              <span className="font-medium text-tinta">
                E-mail <span className="font-normal text-cinza">(opcional)</span>
              </span>
              <input
                className={CAMPO}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                maxLength={200}
                placeholder="contato@cliente.com.br"
              />
            </label>
          </div>
          {erro && <p className="pt-3 text-sm text-erro-tinta">{erro}</p>}
          <div className="flex flex-wrap gap-2 pt-4">
            <button
              type="button"
              onClick={() => void salvar()}
              disabled={salvando}
              className="rounded-full bg-laranja px-5 py-2.5 text-sm font-semibold text-navy-ink transition hover:brightness-105 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-laranja/40"
            >
              {salvando ? "Salvando…" : "Salvar"}
            </button>
            <button
              type="button"
              onClick={() => setEditando(null)}
              className="rounded-full px-5 py-2.5 text-sm font-medium text-cinza transition hover:text-tinta"
            >
              Cancelar
            </button>
          </div>
        </section>
      )}

      {clientes.length === 0 ? (
        <p className="rounded-xl bg-superficie-2 p-6 text-sm leading-relaxed text-cinza">
          Você ainda não cadastrou nenhum cliente. O calendário funciona sem eles — o
          cliente serve para filtrar por regime e marcar o que já foi entregue.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {clientes.map((c) => (
            <li
              key={c.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border-[1.5px] border-borda bg-superficie p-4"
            >
              <div className="min-w-0">
                <p className="font-medium text-tinta">{c.nome}</p>
                <p className="pt-0.5 text-xs text-cinza">
                  {rotuloDoRegime(c.regime)}
                  {c.telefone ? ` · ${mascararTelefone(c.telefone)}` : ""}
                  {c.email ? ` · ${c.email}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => abrir(c)}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-link transition hover:bg-chip"
                >
                  Editar
                </button>
                {confirmando === c.id ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void excluir(c.id)}
                      disabled={salvando}
                      className="rounded-lg bg-erro-fundo px-3 py-1.5 text-xs font-semibold text-erro-tinta transition disabled:opacity-60"
                    >
                      Excluir mesmo
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmando(null)}
                      className="rounded-lg px-3 py-1.5 text-xs font-medium text-cinza"
                    >
                      Não
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmando(c.id)}
                    className="rounded-lg px-3 py-1.5 text-xs font-medium text-cinza transition hover:text-erro-tinta"
                  >
                    Excluir
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
